import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { Edit, Trash, More } from "@rsuite/icons";
import BlockRoundIcon from "@rsuite/icons/BlockRound";
import CheckRoundIcon from "@rsuite/icons/CheckRound";
import React, { useRef, useState } from "react";
import { Popover, Dropdown, IconButton, Whisper } from "rsuite";
import ViewCompanyModal from "./ViewCompany";
import { useDispatch } from "react-redux";
import { setActionData } from "../../../redux/userActionData";
import { useNavigate } from "react-router-dom";
import { roleObjArr, statusArr } from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { encryptUrl } from "../../../navigation/useDecryptParams";
import { ContentRoutes } from "../../../navigation/NavConstant";
import UserForCompany from "../Users/UserForCompany";
import CompanyMasterRule from "./CompanyMasterRule";
import CompanyNetwork from "./CompanyNetwork";
import { isEmptyVal } from "../../../utils/helpers";

const CompanySingleAction = ({
  companyData,
  onDeleteCompany,
  onPublishCompany,
  permission,
  refreshList,
  ...props
}: {
  companyData: any;
  onDeleteCompany: Function;
  onPublishCompany: Function;
  permission: any;
  refreshList?: Function;
}) => {
  const { t } = useTranslation();
  const compRef = useRef({});
  const dispatch = useDispatch();
  const [openModalByName, setOpenModalByName] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [publishMessage, setPublishMessage] = useState("");
  const ecryptedCompId = encryptUrl(`${companyData.id}`);
  const companyUpdateLink = ContentRoutes.UPDATE_COMPANY.replace(
    ":id",
    ecryptedCompId.toString()
  );

  const productImportLink = ContentRoutes.IMPORT_PRODUCTS_FOR_COMPANY.replace(
    ":id",
    ecryptedCompId.toString()
  );

  const navigate = useNavigate();
  const publishText =
    companyData.status == statusArr.active
      ? t("UNPUBLISH").toLowerCase()
      : t("PUBLISH").toLowerCase();
  const pMessage = t("CONFIRM_ACTION", {
    action:
      companyData.status == statusArr.active
        ? t("UNPUBLISH").toLowerCase()
        : t("PUBLISH").toLowerCase(),
    entity: t("COMPANY").toLowerCase(),
  });
  const publishIcon =
    companyData.status == statusArr.inactive ? (
      <BlockRoundIcon />
    ) : (
      <CheckRoundIcon />
    );

  const renderMenu = (
    {
      onClose,
      left,
      top,
      className,
    }: { onClose: any; left: any; top: any; className: any },
    ref:
      | ((instance: HTMLDivElement | null) => void)
      | React.RefObject<HTMLDivElement>
      | null
      | undefined
  ) => {
    const handleSelect = (eventKey: any) => {
      if (eventKey == "import-product") {
        handleProductImportAction();
      } else {
        setOpenModalByName(eventKey);
        dispatch(setActionData(companyData));
      }

      onClose();
    };
    return (
      <Popover ref={ref} className={className} style={{ left, top }} full>
        <Dropdown.Menu onSelect={handleSelect}>
          <Dropdown.Item eventKey={"view-company"}>{t("VIEW")}</Dropdown.Item>
          {permission.update && (
            <>
              <Dropdown.Item eventKey={"update-master-rule"}>
                {isEmptyVal(companyData?.master_rule_id)
                  ? t("CREATE")
                  : t("UPDATE")}{" "}
                {t("MASTER") + " " + t("RULE")}
              </Dropdown.Item>
              <Dropdown.Item eventKey={"store-network"}>
                {t("COMPANY") + " " + t("NETWORK")}
              </Dropdown.Item>
            </>
          )}
          {permission.create_user && (
            <Dropdown.Item eventKey={"set-primary-user"}>
              {companyData?.primary_user_id
                ? t("UPDATE") + " " + t("PRIMARY") + " " + t("ADMIN")
                : t("CREATE") + " " + t("PRIMARY") + " " + t("ADMIN")}
            </Dropdown.Item>
          )}

          {permission.import_product && (
            <Dropdown.Item eventKey={"import-product"}>
              {t("IMPORT") + " " + t("PRODUCT")}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Popover>
    );
  };

  function handleEditAction() {
    navigate(`${companyUpdateLink}`);
  }

  function handleProductImportAction() {
    navigate(`${productImportLink}`);
  }

  function onCompletePrimaryUser() {
    if (refreshList) refreshList();
  }

  return (
    <div>
      {permission.update && (
        <>
          <IconButton
            appearance="subtle"
            onClick={handleEditAction}
            title={t("UPDATE")}
            icon={<Edit />}
          />
        </>
      )}
      {permission.delete && (
        <>
          <IconButton
            onClick={() => {
              compRef.current = companyData;
              setOpenDeleteModal(true);
            }}
            icon={<Trash />}
            title={t("DELETE")}
            size="md"
          />
        </>
      )}
      {permission.publish && (
        <>
          <IconButton
            onClick={() => {
              compRef.current = companyData;
              setOpenPublishModal(true);
              setPublishMessage(pMessage);
            }}
            icon={publishIcon}
            title={publishText}
            size="md"
          />
        </>
      )}
      <Whisper
        placement="topEnd"
        trigger="click"
        speaker={renderMenu}
        preventOverflow={true}
      >
        <IconButton appearance="subtle" icon={<More />} />
      </Whisper>
      {openModalByName === "view-company" && (
        <ViewCompanyModal
          status={openModalByName === "view-company" ? true : false}
          toggle={setOpenModalByName}
        />
      )}
      {openModalByName === "set-primary-user" && companyData.id && (
        <UserForCompany
          companyId={companyData.id}
          forRole={roleObjArr.client_admin.id}
          openModal={true}
          setOpenModal={setOpenModalByName}
          onComplete={onCompletePrimaryUser}
        />
      )}
      {openModalByName === "update-master-rule" && companyData.id && (
        <CompanyMasterRule
          companyId={companyData.id}
          openModal={true}
          setOpenModal={setOpenModalByName}
        />
      )}
      {openModalByName === "store-network" && companyData.id && (
        <CompanyNetwork
          companyId={companyData.id}
          openModal={true}
          setOpenModal={setOpenModalByName}
        />
      )}
      <AlertModal
        open={openDeleteModal}
        handleClose={(e: any) => {
          if (e == true) {
            onDeleteCompany(compRef);
            setOpenDeleteModal(false);
          } else {
            setOpenDeleteModal(false);
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("COMPANY").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
      <AlertModal
        open={openPublishModal}
        handleClose={(e: any) => {
          if (e == true) {
            onPublishCompany(compRef);
            setOpenPublishModal(false);
          } else {
            setOpenPublishModal(false);
          }
        }}
        displayText={publishMessage}
      />
    </div>
  );
};

export default CompanySingleAction;
